import { useEffect, useState } from "react";

import { type Compare } from "@features/sales-v3/hooks/use-chart-state";
import { type GetCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import {
  CustomMetricSearch,
  getExcludedFrequentlyUsedMetrics,
  getFrequentlyUsedMetrics,
} from "@features/sales-v3/utils/frequently-used-metrics";
import {
  safePolygon,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { type SalesPageMetric } from "@north-beam/nb-client-side";

import { MetricSearch } from "../metric-filters/metric-search";

import { MenuItem } from "./menu-item";
import { MenuSubmenuItem } from "./menu-submenu-item";

interface MetricSelectorProps {
  compare: Compare;
  updateCompare: (compare: Compare) => void;
  metrics: SalesPageMetric[];
  getCompareQueryMetadata: GetCompareQueryMetadata;
  isDisabled?: boolean;
}

export const MetricSelector = ({
  compare,
  updateCompare,
  metrics,
  getCompareQueryMetadata,
  isDisabled = false,
}: MetricSelectorProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isHovering) {
      setSearch("");
    }
  }, [isHovering]);

  const compareQueryMetadata = getCompareQueryMetadata({
    queryKey: "metricName",
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isHovering,
    onOpenChange: setIsHovering,
    transform: false,
    placement: "right-start",
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const frequentlyUsedMetrics = getFrequentlyUsedMetrics(metrics);
  const filteredFrequentlyUsedMetrics = new CustomMetricSearch(
    frequentlyUsedMetrics.map((metric) => ({
      label: metric.name,
      value: metric,
    })),
  ).search(search);

  const otherMetrics = getExcludedFrequentlyUsedMetrics(metrics);
  const filteredOtherMetrics = new CustomMetricSearch(
    otherMetrics.map((metric) => ({
      label: metric.name,
      value: metric,
    })),
  ).search(search);

  const hasNoResults =
    filteredFrequentlyUsedMetrics.length === 0 &&
    filteredOtherMetrics.length === 0;

  return (
    <div>
      <MenuItem
        isDisabled={isDisabled}
        isHovering={isHovering}
        icon={<span className="mr-2">{compareQueryMetadata.icon}</span>}
        text={compareQueryMetadata.label}
        setReference={refs.setReference}
        {...getReferenceProps()}
      />
      {isHovering && (
        <div
          className="absolute rounded-md bg-white elevation-1 pt-3 pb-1 z-10 text-sm w-56"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <MetricSearch value={search} onChange={setSearch} />
          {hasNoResults && (
            <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
          )}
          <div
            className="overflow-y-auto max-h-56"
            style={{
              minHeight: "56px",
            }}
          >
            {filteredFrequentlyUsedMetrics.length > 0 && (
              <div className="text-xxs text-gray+1 px-4 pt-2 pb-1">
                Frequently used
              </div>
            )}
            {filteredFrequentlyUsedMetrics.map(({ value }) => (
              <MetricRow
                key={value.id}
                metric={value}
                updateCompare={updateCompare}
                compare={compare}
              />
            ))}
            <hr className="my-2" />
            {filteredOtherMetrics.map(({ value }) => (
              <MetricRow
                key={value.id}
                metric={value}
                updateCompare={updateCompare}
                compare={compare}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MetricRow = ({
  metric,
  updateCompare,
  compare,
}: {
  metric: SalesPageMetric;
  updateCompare: (compare: Compare) => void;
  compare: Compare;
}) => {
  const handleMetricChange = (metric: SalesPageMetric) => {
    updateCompare({
      ...compare,
      queryKey: "metricName",
      queryValue: metric.id,
    });
  };

  return (
    <MenuSubmenuItem
      key={metric.name}
      onClick={() => handleMetricChange(metric)}
      label={metric.name}
      isSelected={
        compare.queryKey === "metricName" && compare.queryValue === metric.name
      }
    />
  );
};
